import moment from 'moment';
import _ from 'lodash';
import { SEARCH_PARAMS_SESSION_PAGE } from '@bld/bld-libs/constants';

import {
  storageSetItemDateRange,
  storageGetItemDateRangeDefault,
} from '../utilities/dateSelectorUtility';
import { util } from '../canvas/common/util';
import { getNextDay } from '../canvas/common/works';
import EventMg, { EventType } from '../utilities/EventManager';
import RoanUtility from '../utilities/roanUtility';
import SessionStorageService from '../services/utilities/SessionStorageService';
import { isPartnerCompanyRoleId, isTargetRoleId } from './roleUtility';
import {
  APPROVAL_STATUS,
  BUILDEE_SERVICES,
  REQUEST_PARAMS,
  USER_ROLES,
  USER_ROLES_IDS,
  FIELD_COMPLETE_STATUS,
  DATE_TIME_FORMAT,
} from './constants';
import { FieldTreeService } from '../services/FieldTrees';
import SessionsService from '../services/Sessions';
import { FieldService } from '../services/Fields';
import UsersApi from '../services/users/UsersApi';
import { hasRoanPermission } from '../utilities/companyUserPermissionUtil';
import { setFieldNameForGTM } from './gtm';
import { setFieldIdToURL } from './commonUtil';

moment.locale('ja');

/**
 * 現場を切り替える時に Storageを更新する処理
 */
export const changeFieldWriteStorage = async ({
  id: fieldId,
  working_time,
  work_start_time,
  work_start_time_night,
  work_start_time_midnight,
  work_end_time,
  work_end_time_night,
  work_end_time_midnight,
  use_gk_flg,
  use_roan_flg,
  use_progress_flg,
  use_tracker_flg,
  company,
  use_partner_company_approval_flag,
  name: fieldName,
  use_lw_cooperation_flag,
  use_direct_cooperation_flag,
  use_ky_flg,
}) => {
  // 現場ID
  SessionStorageService.setItem('field_id', fieldId);
  setFieldNameForGTM(fieldName);
  // 初期表示日を稼働日翌日にする
  const workNextDay = moment(
    util.dataFormat(await getNextDay(fieldId), 'YYYY/MM/DD'),
    'YYYY/MM/DD'
  ).format('YYYY/MM/DD');

  // 期間|日付選択 と 作業時間帯 昼|夜|深夜 はデフォルト値にする
  const defaultParam = storageGetItemDateRangeDefault();
  storageSetItemDateRange({
    fromDate: workNextDay,
    toDate: workNextDay,
    workHourZone: defaultParam.workHourZone,
  });

  // 作業時間
  SessionStorageService.setItem('working_time', working_time);

  // 作業開始時間 昼, 夜, 深夜
  SessionStorageService.setItem('work_start_time', work_start_time);
  SessionStorageService.setItem('work_start_time_night', work_start_time_night);
  SessionStorageService.setItem(
    'work_start_time_midnight',
    work_start_time_midnight
  );
  // 作業終了時間 昼, 夜, 深夜
  SessionStorageService.setItem('work_end_time', work_end_time);
  SessionStorageService.setItem('work_end_time_night', work_end_time_night);
  SessionStorageService.setItem('work_end_time_midnight', work_end_time_midnight);

  // 協力会社承認機能利用状況
  SessionStorageService.setUsePartnerCompanyApprovalFlag(
    use_partner_company_approval_flag
  );

  // 入退場・労務安全利用状況
  SessionStorageService.setItem('use_gk_flg', use_gk_flg);
  SessionStorageService.setItem('use_roan_flg', use_roan_flg);
  SessionStorageService.setItem('use_progress_flg', use_progress_flg);
  SessionStorageService.setItem('use_tracker_flg', use_tracker_flg);

  SessionStorageService.setUseKYFlag(use_ky_flg);
  // 現場を変更する際にセッションストレージに保存している検索条件をクリアする
  SessionStorageService.removeItem(SEARCH_PARAMS_SESSION_PAGE.KY_WORK_FINISH_REPORT);
  SessionStorageService.removeItem(SEARCH_PARAMS_SESSION_PAGE.KY_REPORT);

  SessionStorageService.setItem('prime_company_id', company.company_id);

  EventMg.trigger(EventType.SELECT_FIELD, fieldId);

  // 労務安全から masterNavi で調整会議に戻ってきた場合、
  // または直接遷移リンクから共通画面への遷移時に労安権限しか持っていなかった場合に設定される値
  // 残っていると × ボタンを押下した際に労務安全が利用できない現場でも労務安全に遷移してしまうので削除しておく
  // 車両管理も同様
  SessionStorageService.removeItem('url');
  SessionStorageService.removeItem('url_tracker');

  // LINE WORKS連携機能利用状況
  SessionStorageService.setUseLwCooperationFlag(use_lw_cooperation_flag);

  // direct連携機能利用状況
  SessionStorageService.setUseDirectCooperationFlag(use_direct_cooperation_flag);

  SessionStorageService.removeReportUnitCode();
};

/**
 * 現場変更後の画面遷移を実行
 * @param {number} serviceId：遷移後に表示したいBuildeeのサービスID（指定がなければ調整会議に遷移する）
 */
export const goChangedFieldPage = serviceId => {
  if (serviceId === BUILDEE_SERVICES.ROAN.ID) {
    // 労務安全のみ遷移方法が違うため最初に判定して処理する
    RoanUtility.setSessionSelectedPartnerCompanyIdBeforeMoveRoan();
    SessionStorageService.removeLastCheckedTimeOfExpiration();
    window.location.replace(setFieldIdToURL(new URL(process.env.REACT_APP_GREENFILE_URL)));
    return;
  }

  let url = '';
  switch (serviceId) {
    case BUILDEE_SERVICES.NYUTAI.ID:
      url = '/gatekeeper/0/0/0/InOutDashboard:1/0:0';
      break;
    case BUILDEE_SERVICES.PROGRESS.ID:
      url = '/progress/0/0/0/ProgressSchedule:1/0:0';
      break;
    case BUILDEE_SERVICES.TRACKER.ID:
      const fieldId = SessionStorageService.getFieldId();
      const companyId = SessionStorageService.getCompanyId();
      url = `/tracker/transportationGroup?__currentFieldId=${fieldId}&__currentCompanyId=${companyId}`;
      break;
    case BUILDEE_SERVICES.CONFERENCE.ID:
    default:
      url = '/conference/0/0/0/Dashboard:1/Dashboard:0';
      break;
  }
  //location.hrefではなくpushStateでURLを変更してからreload
  //原因不明だがIE11でwindow.location.hrefを繰り返すとメモリが増加し続ける
  //reloadすると600MBくらいで開放される挙動になる
  window.history.pushState(null, null, url);
  window.location.reload();
};

/**
 * 現場一覧の初期検索パラメータ 現場一覧とヘッダの現場選択で共通
 */
export const createFieldsDefaultSearchParams = () => {
  return {
    company_id: isTargetRoleId(USER_ROLES_IDS.PARTNER_COMPANY)
      ? null
      : SessionStorageService.getItem('company_id'),
    company_branch_id: null,
    usage_status: [0, 1],
    ccus_register_status: [],
    offset: 0,
    limit: REQUEST_PARAMS.parentLimit,
    approval_flag: APPROVAL_STATUS.approval.id, // 認証済みの現場だけ表示
    order: 'groundbreaking_date_desc',
  };
};

/**
 * 選択した現場情報をサーバやセッションに保存する
 * @param {number}fieldId：選択した現場ID
 * @param {number}roleId: ロールID
 * @return {boolean} 各処理が正常に完了したかどうかを返却する
 */
export const saveFieldData = async (fieldId, roleId) => {
  if (isPartnerCompanyRoleId(roleId)) {
    try {
      const response = await FieldTreeService.partnerUserSelfIds(fieldId);
      if (!response.result) {
        return false;
      } else {
        const entity = response.data.entities;
        const fieldTreeId = entity.field_tree.id;
        const fieldTreeUserId = entity.field_tree_user.id;
        SessionStorageService.setFieldTreeId(fieldTreeId);
        SessionStorageService.setFieldTreeUserId(fieldTreeUserId);

        if (isTargetRoleId([USER_ROLES.foreman], roleId)) {
          try {
            const fieldTreeRes = await FieldTreeService.show(
              fieldId,
              fieldTreeId,
              fieldTreeUserId
            );
            if (fieldTreeRes.result) {
              const fieldTreeUser = fieldTreeRes.data.entities.field_tree_user;
              if (!_.isEmpty(fieldTreeUser.field_tree_user_jobs)) {
                SessionStorageService.setJobId(
                  fieldTreeUser.field_tree_user_jobs[0].job_id
                );
              }
            }
          } catch (e) {
            console.error(e);
            return false;
          }
        }
      }
    } catch (e) {
      console.error(e);
      return false;
    }
  }

  try {
    const sessionRes = await SessionsService.updatefield(fieldId);
    if (!sessionRes.result) {
      console.error(
        '選択現場の更新に失敗しました',
        'Fields.jsx',
        'setFieldData',
        'SessionsService.updatefield',
        sessionRes
      );
      return false;
    }
  } catch (e) {
    console.error(e);
    return false;
  }

  try {
    const fieldRes = await FieldService.info(fieldId);
    if (!fieldRes.result) {
      console.error(
        'Fields.jsx',
        'setFieldData',
        'FieldService.info !response.result',
        fieldRes
      );
      return false;
    } else {
      await changeFieldWriteStorage(fieldRes.data.entities.field);
    }
  } catch (e) {
    console.error(e);
    return false;
  }

  return true;
};

/**
 * 現場情報から利用可能なBuildeeのサービスIDを取得する
 * @param {object}fieldData 現場除法
 * @param {number}companyId 会社ID
 * @return {?number} 利用可能なBuildeeのサービスID 利用可能サービスがなければnullを返却
 */
export const getAvailableServiceId = async (fieldData, companyId) => {
  let serviceId = null;
  try {
    const response = await UsersApi.fetchCompanyUserPermission(companyId);
    if (response.result) {
      if (response.data.entities.company_user_permission.use_chosei_flg) {
        // 調整会議が利用可能な場合は調整会議のサービスIDを設定する
        // 調整会議は現場毎にON/OFFはしないため現場情報は参照しない
        serviceId = BUILDEE_SERVICES.CONFERENCE.ID;
      } else if (
        fieldData.use_roan_flg &&
        response.data.entities.company_user_permission.use_green_file_flg
      ) {
        // 現場が労務安全を利用しており、ユーザーにも労務安全のサービス権限がある場合は労務安全のサービスIDを設定する
        serviceId = BUILDEE_SERVICES.ROAN.ID;
      } else if (
        fieldData.use_gk_flg &&
        response.data.entities.company_user_permission.use_nyutaijo_flg
      ) {
        // 現場が入退場を利用しており、ユーザーにも入退場のサービス権限がある場合は入退場のサービスIDを設定する
        serviceId = BUILDEE_SERVICES.NYUTAI.ID;
      }
    } else {
      console.warn(
        'filedUtil.js getAvailableServiceId.fetchCompanyUserPermission false',
        response
      );
    }
  } catch (error) {
    console.warn(
      'filedUtil.js getAvailableServiceId.fetchCompanyUserPermission else',
      error
    );
  }

  return serviceId;
};

/**
 * 労安を利用している現場かつ労務安全の権限がある場合はtrue
 */
export const hasRoanFieldPermission = async (company_id, field_id) => {
  let roanPermission = false;
  roanPermission = await hasRoanPermission(company_id);
  if (!roanPermission) {
    return false;
  }

  const loadListParam = {
    ...createFieldsDefaultSearchParams(),
    field_id,
    use_roan_flg: 1,
  };

  let fields = [];
  await FieldService.list(loadListParam).then(response => {
    if (response.result) {
      fields = response.data.entities.fields;
    }
  });
  if (_.size(fields) < 1) {
    return false;
  }

  return true;
};

export const getFieldStatus = ({ complete_schedule_date, usage_end_date }) => {
  const dateFormat = DATE_TIME_FORMAT.slashDateFormat;
  const today = moment();
  const completeScheduleDate = moment(complete_schedule_date, dateFormat);
  const usageEndDate = moment(usage_end_date, dateFormat);

  if (usageEndDate.isBefore(today, 'day')) {
    return FIELD_COMPLETE_STATUS.EXPIRED;
  }

  if (today.isSameOrBefore(usageEndDate, 'day')) {
    if (completeScheduleDate.isBefore(today, 'day')) {
      return FIELD_COMPLETE_STATUS.COMPLETED;
    }

    if (today.isBefore(completeScheduleDate, 'day')) {
      return FIELD_COMPLETE_STATUS.RUNNING;
    }
  }

  return FIELD_COMPLETE_STATUS.RUNNING;
};



// WEBPACK FOOTER //
// ./src/utilities/fieldUtil.js